/* eslint-disable */
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppDataProvider, useAppData } from './context/AppDataContext';
import { BillingProvider } from './components/billing/BillingContext';
import Layout from './components/layout/Layout';
import Pricing from './components/billing/Pricing';
import ContactForm from './components/billing/ContactForm';
import { BillingSuccess, BillingCancelled } from './components/billing/BillingSettings';
import Login from './components/auth/Login';
import MustChangePassword from './components/auth/MustChangePassword';
import AcceptLegalTerms from './components/auth/AcceptLegalTerms';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import ResendVerification from './components/auth/ResendVerification';
import Dashboard from './components/dashboard/Dashboard';
import Properties from './components/properties/Properties';
import Companies from './components/companies/Companies';
import Transactions from './components/transactions/Transactions';
// COMPANY-006: CompanyExpenses is no longer routed — Transactions covers it.
// The component file is deliberately left in the repo for one release as a
// rollback path; delete it once the change has held.
import Projects from './components/projects/Projects';
import Calendar from './components/calendar/Calendar';
import Prospects from './components/prospects/Prospects';
import Reports from './components/reports/Reports';
import Admin from './components/admin/Admin';
import Settings from './components/settings/Settings';
import Tenants from './components/tenants/Tenants';
import TenantDetail from './components/tenants/TenantDetail';
import Contractors from './components/contractors/Contractors';
import ServiceRequests from './components/service-requests/ServiceRequests';
import QuoteSubmission from './components/service-requests/QuoteSubmission';
import PrivacyPolicy from './components/legal/PrivacyPolicy';
import TermsOfService from './components/legal/TermsOfService';
import EULA from './components/legal/EULA';
import VerifyEmail from './components/auth/VerifyEmail';
import './styles/global.css';

// Protected route wrapper
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="loading-overlay">
        <div className="loading-spinner"></div>
        <p style={{ marginTop: '1rem', color: 'var(--text-secondary)' }}>Loading...</p>
      </div>
    );
  }
  
  if (!user) return <Navigate to="/login" replace />;
  // ADMIN-CREATE-001: Accounts created directly by an admin must set their own
  // password before doing anything else in the app — the whole app is gated
  // behind this screen until it's cleared.
  if (user.mustChangePassword) return <MustChangePassword username={user.username} />;
  // LEGAL-002: Any user whose accepted legal version doesn't match the
  // current one (existing users after a document update — new
  // registrations and admin-created accounts accept during their own
  // signup/setup flow) must accept before continuing.
  if (user.needsLegalAcceptance) return <AcceptLegalTerms username={user.username} />;
  return children;
}

// App with data loading
function AppContent() {
  const { user } = useAuth();
  const { loadAllData } = useAppData();

  useEffect(() => {
    if (user) loadAllData();
  }, [user, loadAllData]);

  return (
    <Routes>
      {/* Public routes — no auth required */}
      <Route path="/login"              element={<Login />} />
      <Route path="/forgot-password"    element={<ForgotPassword />} />
      <Route path="/reset-password"     element={<ResetPassword />} />
      <Route path="/resend-verification" element={<ResendVerification />} />
      <Route path="/pricing"            element={<Pricing />} />
      <Route path="/contact"            element={<ContactForm />} />
      <Route path="/billing/success"    element={<BillingSuccess />} />
      <Route path="/billing/cancelled"  element={<BillingCancelled />} />

      {/* Protected app routes */}
      <Route path="/" element={
        <ProtectedRoute>
          <Layout><Dashboard /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/properties/*" element={
        <ProtectedRoute>
          <Layout><Properties /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/transactions" element={
        <ProtectedRoute>
          <Layout><Transactions /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/companies" element={
        <ProtectedRoute>
          <Layout><Companies /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/tenants" element={
        <ProtectedRoute>
          <Layout><Tenants /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/tenants/:id" element={
        <ProtectedRoute>
          <Layout><TenantDetail /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/contractors" element={
        <ProtectedRoute>
          <Layout><Contractors /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/service-requests" element={
        <ProtectedRoute>
          <Layout><ServiceRequests /></Layout>
        </ProtectedRoute>
      } />
      {/* COMPANY-006: retired tab. Kept as a redirect rather than deleted so
          existing bookmarks and any lingering in-app links don't 404 —
          they land on Transactions filtered to company expenses. */}
      <Route path="/company" element={<Navigate to="/transactions?filter=company" replace />} />
      <Route path="/projects/*" element={
        <ProtectedRoute>
          <Layout><Projects /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/calendar" element={
        <ProtectedRoute>
          <Layout><Calendar /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/prospects" element={
        <ProtectedRoute>
          <Layout><Prospects /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/reports" element={
        <ProtectedRoute>
          <Layout><Reports /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/admin" element={
        <ProtectedRoute>
          <Layout><Admin /></Layout>
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <Layout><Settings /></Layout>
        </ProtectedRoute>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        {/* Fully public — outside auth providers entirely */}
        <Route path="/quote/:token" element={<QuoteSubmission />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/eula" element={<EULA />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        {/* All other routes inside auth providers */}
        <Route path="*" element={
          <AuthProvider>
            <AppDataProvider>
              <BillingProvider>
                <AppContent />
              </BillingProvider>
            </AppDataProvider>
          </AuthProvider>
        } />
      </Routes>
    </BrowserRouter>
  );
}
